import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import {createCargoCorpRepository} from '@yandex-delivery/api/repo/cargo-corp/external'
import {createCargoCrmRepository} from '@yandex-delivery/api/repo/cargo-crm'
import {createDCAARepository} from '@yandex-delivery/api/repo/dcaa'
import {createPassportRepository} from '@yandex-delivery/api/repo/passport'
import {createPaymentCardService} from '@yandex-delivery/api/service/payment'
import {createRegistrationService} from '@yandex-delivery/api/service/registration'
import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import {fetch as fetchBase, FetchError, ServerError} from '@yandex-delivery/core/utils/network'
import type {LaunchResponse} from '@yandex-delivery/types/schemas/services/cargo-corp/docs/yaml/definitions/launch'
import type {
  PaymentMethodsListRequest,
  PaymentMethodsListResponse,
} from '@yandex-delivery/types/schemas/services/delivery-corp-account-api/definitions/payment_methods'
import {addQuery, constant, firstOf, flow, get, ifThen, isEmpty, pipe} from '@yandex-delivery/utils'
import {useCallback} from 'react'

import {useConfigContext} from '../../contexts/ConfigContext'
import {FetchFunction, withCargoApiUrlMiddleware, withCsrfMiddleware} from '../fetch'
import {useFetch} from './useFetch'

const fetch = pipe(fetchBase, withCsrfMiddleware) as typeof fetchBase
const cargoCorpFetch = pipe(fetch as FetchFunction, withCargoApiUrlMiddleware) as typeof fetchBase

const cargoCrmRepository = createCargoCrmRepository({
  fetch: cargoCorpFetch,
})
const cargoCorpRepository = createCargoCorpRepository({
  fetch,
})
const dcaaRepository = createDCAARepository({fetch})

function fetchPaymentMethods(body: PaymentMethodsListRequest, country?: string): Promise<PaymentMethodsListResponse> {
  return fetch<PaymentMethodsListRequest, PaymentMethodsListResponse, PaymentMethodsListResponse>({
    method: 'POST',
    url: addQuery(externalApiURL('cargo/v1/corp/employee/payment-methods/list'), {country}),
    body,
  })
}

export const enum PaymentCardErrorCode {
  UNAUTHORIZED = 'unauthorized',
  EMPTY_LOGIN = 'empty_login',
  EMPTY_PHONE = 'empty_phone',
}

const isEmptyLaunch: (r?: LaunchResponse) => boolean = isEmpty
const isPhoneEmpty: (r?: LaunchResponse) => boolean = flow(get('passport_account.details.phones'), isEmpty)

const getUnauthorizedMessage: (r?: LaunchResponse) => PaymentCardErrorCode | undefined = firstOf(
  ifThen(isEmptyLaunch, constant(PaymentCardErrorCode.UNAUTHORIZED)),
  ifThen(isPhoneEmpty, constant(PaymentCardErrorCode.EMPTY_PHONE)),
)

type PaymentCardRequestProps = {
  onSuccess(card: PaymentCard): void | Promise<void>
}

export const usePaymentCardService = () => {
  const {countryCode, launch, trustHost} = useConfigContext()

  const {fetchCardBoundContext, fetchBoundedCard, fetchCards, fetchNotificationsAgreements} = createPaymentCardService({
    cargoCorpRepository,
    dcaaRepository,
    config: {
      country: countryCode,
      trustDomain: trustHost,
    },
  })

  const fetchPaymentCard = useCallback<(props: PaymentCardRequestProps) => Promise<PaymentCard | undefined>>(
    async ({onSuccess}) => {
      const cardErrorCode = getUnauthorizedMessage(launch)

      // если ланч пустой, значит пользователь не авторизован и смысла в походе в бекенд нет
      if (cardErrorCode) {
        const error = new FetchError(cardErrorCode, {
          text: cardErrorCode,
        })

        // @ts-expect-error Недостаточно данных для конструктора данной ошибки
        throw new ServerError({
          error,
          status: 401,
        })
      }

      const [boundedCard, paymentMethod] = await Promise.all([
        fetchBoundedCard(),
        fetchPaymentMethods({mode: 'employee'}, countryCode),
      ])

      const attachedPaymentMethod = paymentMethod?.methods.find(method => method.is_selected)

      if (boundedCard) {
        const card = Object.assign(boundedCard, {
          method_id: attachedPaymentMethod?.id,
          type: attachedPaymentMethod?.display?.type,
          kind: paymentMethod?.selected_method.payment_method_kind,
          has_disable_reason: attachedPaymentMethod?.details.is_disabled,
        })

        await onSuccess(card)

        return card
      }

      return boundedCard
    },
    [countryCode, fetchBoundedCard, launch],
  )

  return {
    fetchCardBoundContext,
    fetchBoundedCard,
    fetchCards,
    fetchPaymentCard,
    fetchPaymentMethods,
    fetchNotificationsAgreements,
  }
}

export const useRegistrationService = () => {
  const {passportFetch} = useFetch()

  const passportRepository = createPassportRepository({
    fetch: passportFetch,
  })

  return createRegistrationService({
    corpRepository: cargoCorpRepository,
    crmRepository: cargoCrmRepository,
    passportRepository,
  })
}
